import {gql} from '@apollo/client';
import {Stack, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import CollaborationManagementContent, {Policy} from 'collaborator-components/CollaborationManagementContent';
import {Collaboration} from 'collaborator-components/useCollaborationManagementState';
import {PolicyAction} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {RoutineRunAs} from 'llm/routines/RoutineTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';
import {listToReadable} from 'util/NameUtil';

import {
  RoutinePolicyListingQuery,
  RoutinePolicyListingQueryVariables,
} from './__generated/RoutinePolicyListingQuery';

type RoutineCreatePolicy = RoutinePolicyListingQuery['policyListing']['policies'][number];

export const ROUTINE_POLICY_LISTING_QUERY = gql`
  query RoutinePolicyListingQuery @engComponent(name: LLM) @skipAudit {
    policyListing(resourceType: ROUTINE) {
      policies {
        __typename
        id
        name
        isHidden
        policyStatements {
          action
        }
      }
    }
  }
`;

export function getUnavailableRoutinePolicyIds(
  policies: Array<RoutineCreatePolicy>,
  runAs: RoutineRunAs
): Array<string> {
  if (runAs === RoutineRunAs.SERVICE_ACCOUNT) {
    return [];
  }
  return policies
    .filter((policy) => policy.policyStatements.some(({action}) => action === PolicyAction.WRITE))
    .map(({id}) => id);
}

interface RoutineCreateCollaboratorsStepProps {
  runAs: RoutineRunAs;
  collaborations: Array<Collaboration>;
  onCollaborationsChange: (collaborations: Array<Collaboration>) => void;
  onCollaboratorsCanSaveChange: (canSave: boolean) => void;
}

export default function RoutineCreateCollaboratorsStep({
  runAs,
  collaborations,
  onCollaborationsChange,
  onCollaboratorsCanSaveChange,
}: RoutineCreateCollaboratorsStepProps): JSX.Element {
  const policyResult = useBenchlingQuery<RoutinePolicyListingQuery, RoutinePolicyListingQueryVariables>(
    ROUTINE_POLICY_LISTING_QUERY,
    {}
  );

  const policies = useMemo(() => {
    if (policyResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return policyResult.data.policyListing.policies satisfies Array<Policy>;
  }, [policyResult]);

  const unavailablePolicyIds = useMemo(
    () => getUnavailableRoutinePolicyIds(policies, runAs),
    [policies, runAs]
  );

  const problemPolicyNames = useMemo(() => {
    const unavailable = new Set(unavailablePolicyIds);
    const names = collaborations
      .map((collaboration) => collaboration.policy)
      .filter((policy) => unavailable.has(policy.id))
      .map((policy) => policy.name);
    return [...new Set(names)];
  }, [collaborations, unavailablePolicyIds]);

  if (policyResult.type === QueryResultType.ERROR) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Could not load Agent access policies"
        supportingInfo="Try reloading the page."
      />
    );
  }

  if (policyResult.type !== QueryResultType.SUCCESS) {
    return <CenteredSpinner />;
  }

  return (
    <Stack spacing="md">
      <Stack spacing={4}>
        <Text size="md" fw={600}>
          Add collaborators
        </Text>
        <Text size="sm" c="dimmed">
          Choose who can use and manage this Agent.
        </Text>
      </Stack>
      {problemPolicyNames.length > 0 && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`${listToReadable(problemPolicyNames)} will have limited value as editing personal agents is restricted`}
        />
      )}
      <CollaborationManagementContent
        ancestorCollaborations={[]}
        collaborations={collaborations}
        setCollaborations={onCollaborationsChange}
        policies={policies}
        unavailablePolicyIds={unavailablePolicyIds}
        allowEditableOwner={false}
        isReadOnly={false}
        renderAuditorColumn={false}
        setCanSave={onCollaboratorsCanSaveChange}
      />
    </Stack>
  );
}
